import React from 'react';
import './Introduction.css';

const Introduction = ({ isReadOnly }) => {
  return (
    <div className="introduction">
      <div className="intro-content">
        <div className="welcome-section">
          <h2>Welcome to the Pre-Onboarding Application</h2>
          <p>
            Thank you for choosing Next Generation Payment Services. This application will help us 
            gather the necessary information to begin your onboarding process.
          </p>
        </div>

        <div className="instructions-section">
          <h3>How to Complete This Application</h3>
          
          <div className="instruction-grid">
            <div className="instruction-item">
              <div className="instruction-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 12L11 14L15 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" strokeWidth="2"/>
                </svg>
              </div>
              <div className="instruction-content">
                <h4>Complete Sections in Any Order</h4>
                <p>You can navigate between sections and complete them in any order that works best for you.</p>
              </div>
            </div>

            <div className="instruction-item">
              <div className="instruction-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H16L21 8V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M17 21V13H7V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M7 3V8H15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <div className="instruction-content">
                <h4>Save Your Progress</h4>
                <p>Click "Save Section" to save your progress in each section. Your data will be preserved for your next visit.</p>
              </div>
            </div>

            <div className="instruction-item">
              <div className="instruction-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 11H15M9 15H15M17 21H7C5.89543 21 5 20.1046 5 19V5C5 3.89543 5.89543 3 7 3H12.5858C12.851 3 13.1054 3.10536 13.2929 3.29289L19.7071 9.70711C19.8946 9.89464 20 10.149 20 10.4142V19C20 20.1046 19.1046 21 18 21H17Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <div className="instruction-content">
                <h4>Resume Anytime</h4>
                <p>You can safely logout and return later to continue where you left off. Your progress is automatically saved.</p>
              </div>
            </div>

            <div className="instruction-item">
              <div className="instruction-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M22 11.08V12C21.9988 14.1564 21.3005 16.2547 20.0093 17.9818C18.7182 19.7088 16.9033 20.9725 14.8354 21.5839C12.7674 22.1953 10.5573 22.1219 8.53447 21.3746C6.51168 20.6273 4.78465 19.2461 3.61096 17.4371C2.43727 15.628 1.87979 13.4905 2.02168 11.3363C2.16356 9.18211 2.99721 7.13314 4.39828 5.49515C5.79935 3.85717 7.69279 2.71867 9.79619 2.24318C11.8996 1.76769 14.1003 1.98245 16.07 2.86" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M22 4L12 14.01L9 11.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <div className="instruction-content">
                <h4>Complete All Sections</h4>
                <p>All sections must be completed and saved before you can submit your application for review.</p>
              </div>
            </div>
          </div>
        </div>

        <div className="sections-overview">
          <h3>Application Sections</h3>
          <div className="sections-list">
            <div className="section-overview-item">
              <span className="section-number">1</span>
              <div className="section-info">
                <h4>Company Details</h4>
                <p>Basic company information, registration details, and addresses</p>
              </div>
            </div>
            <div className="section-overview-item">
              <span className="section-number">2</span>
              <div className="section-info">
                <h4>Contact Information</h4>
                <p>Primary contact person details and contact information</p>
              </div>
            </div>
            <div className="section-overview-item">
              <span className="section-number">3</span>
              <div className="section-info">
                <h4>PEP Information</h4>
                <p>Politically Exposed Persons declaration and details</p>
              </div>
            </div>
            <div className="section-overview-item">
              <span className="section-number">4</span>
              <div className="section-info">
                <h4>Sanctions & AML-FT</h4>
                <p>Sanctions status and anti-money laundering information</p>
              </div>
            </div>
            <div className="section-overview-item">
              <span className="section-number">5</span>
              <div className="section-info">
                <h4>Business Presentation</h4>
                <p>Detailed business operations and risk assessment</p>
              </div>
            </div>
            <div className="section-overview-item">
              <span className="section-number">6</span>
              <div className="section-info">
                <h4>Documentation</h4>
                <p>Upload required legal and compliance documents</p>
              </div>
            </div>
          </div>
        </div>

        <div className="next-steps">
          <h3>Ready to Begin?</h3>
          <p>
            Use the navigation on the right to move between sections, or click "Next" below to start 
            with the Company Details section.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Introduction;
